import {
  Button,
  Col,
  ListFlatMultipleCheck,
  ListFlatMultipleCheckType,
  PagingInType,
  Popover,
  Search,
  SearchType,
  Zone,
} from '@gimlite/ui-v1';
import { useMemo } from 'react';
import { PopupInfo } from '../../components/popup-info/popup-info.component';
import './multiple-check.form.scss';
import { useTranslation } from '@gimlite/common';

export declare namespace MultipleCheckFormType {
  type Props = {
    handleEvent: {
      search: SearchType.HandleEvent.Search['function'];
      paging: ListFlatMultipleCheckType.HandleEvent.Paging['function'];
      checkList: (value: MultipleCheckFormType.Data.Checked[]) => void;
    };
    data: {
      checked: MultipleCheckFormType.Data.Checked[];
      saved: ListFlatMultipleCheckType.Data.Line<any>[];
      fresh?: ListFlatMultipleCheckType.Data.Line<any>[];
      paging?: PagingInType;
    };
    config: {
      searchPlaceholder: string;
    };
  };

  namespace Data {
    type Line = {
      _id: ListFlatMultipleCheckType.Data.Line<any>['_id'];
      items: {
        component: ListFlatMultipleCheckType.Data.Item;
      };
    };

    type Checked = string;
  }
}

export const MultipleCheckForm = ({
  handleEvent: { paging, checkList, search },
  data,
  config: { searchPlaceholder },
}: MultipleCheckFormType.Props) => {
  const dataFresh = data?.fresh;
  const dataSaved = data?.saved;
  const dataChecked = data?.checked;
  const dataPaging = data?.paging;
  const allData = useMemo(() => {
    return [...dataSaved, ...(dataFresh ? dataFresh : [])].reduce(
      (acc: ListFlatMultipleCheckType.Data.Line<any>[], { _id, ...rest }) => {
        if (!acc.find((element) => element._id === _id)) {
          return [...acc, { _id, ...rest }];
        }
        return acc;
      },
      [],
    );
  }, [dataSaved, dataFresh]);

  const { t, lang } = useTranslation();
  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 0,
        },
        zones: [
          ['selection', 'search'],
          ['list', 'list'],
        ],
        rows: ['min-content', '1fr'],
        columns: ['1fr', 'min-content'],
      }}
    >
      <Zone.Area config={{ area: 'selection' }}>
        <Col config={{ vertical: 'end', horizontal: 'start' }}>
          <Popover
            config={{ minWidth: 'medium' }}
            data={
              <PopupInfo
                data={{
                  node: (
                    <ListFlatMultipleCheck<any>
                      config={{
                        showOnlyCheck: true,
                        border: true,
                        pagination: 'none',
                        columns: [{ key: 'component', size: '1fr' }],
                      }}
                      handleEvent={{
                        paging,
                        click: () => {},
                        checkList,
                      }}
                      data={{
                        checkList: dataChecked,
                        list: allData.reduce(
                          (
                            acc: ListFlatMultipleCheckType.Data.Line<any>[],
                            { _id, ...rest },
                          ) => {
                            if (dataChecked.includes(_id)) {
                              return [...acc, { _id, ...rest }];
                            }

                            return acc;
                          },
                          [],
                        ),
                      }}
                    />
                  ),
                }}
                config={{
                  title: t('currentSelections'),
                  description: {
                    key: 'total',
                    label: t('total'),
                    value: `${dataChecked.length}`,
                  },
                }}
              />
            }
          >
            <Button
              config={{
                size: 'small',
                mode: 'stroke',
                text: t('SEE_SELECTIONS').replace(
                  '[count]',
                  `${dataChecked.length}`,
                ),
              }}
            ></Button>
          </Popover>
        </Col>
      </Zone.Area>
      <Zone.Area config={{ area: 'search' }}>
        <Col config={{ vertical: 'end' }}>
          <Search
            handleEvent={{
              search,
              clear: () => search(''),
            }}
            config={{
              height: 'xsmall',
              width: 'xlarge',
              placeholder: searchPlaceholder,
            }}
          />
        </Col>
      </Zone.Area>
      <Zone.Area config={{ area: 'list' }}>
        <ListFlatMultipleCheck<any>
          config={{
            border: true,
            pagination: 'scroll',
            columns: [{ key: 'component', size: '1fr' }],
          }}
          handleEvent={{
            paging,
            checkList,
          }}
          data={{
            checkList: dataChecked,
            list: dataFresh,
            paging: dataPaging,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
